/* eslint-disable no-mixed-operators */
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

// Kurumsal Hafıza açık teması
const THEME = {
  scene: "#e8eef4",
  ground: "#d3dce5",
  edge: "#3c5166",
  wall: "#7d8f9c",
  accent: "#d9822b",
};

const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]);

// ipucu ve ölçü yazıları (müşteri sayfası İngilizce de gösterebilir)
const METIN = {
  tr: { parca: "parça", kucuk: "küçük paket", kesit: "mm kesit", sira: "yükleme sırası", tavan: (mm) => `Tavana ${mm} mm` },
  en: { parca: "pcs", kucuk: "small bundle", kesit: "mm section", sira: "loading step", tavan: (mm) => `${mm} mm to roof` },
};

// arka arkaya paketler arasında görünen aralık (m); plan ölçülerini değiştirmez
const ARALIK = 0.12;

const fmt = (v, d = 0) => (Number.isFinite(v) ? v.toLocaleString("tr-TR", { minimumFractionDigits: d, maximumFractionDigits: d }) : "");



function makeHexGeometry() {
  const shape = new THREE.Shape();
  for (let i = 0; i < 6; i++) {
    const a = (i * Math.PI) / 3;
    const x = 0.5 * Math.cos(a), y = 0.5 * Math.sin(a);
    i ? shape.lineTo(x, y) : shape.moveTo(x, y);
  }
  shape.closePath();
  const g = new THREE.ExtrudeGeometry(shape, { depth: 1, bevelEnabled: false });
  g.rotateY(Math.PI / 2);
  g.translate(-0.5, 0, 0);
  return g; // en 1 (z), yükseklik 0.866 (y), boy 1 (x)
}
const HEX_H = Math.sqrt(3) / 2;

function capTexture(k, color) {
  const W = 256, H = Math.round(256 * 0.877);
  const cv = document.createElement("canvas"); cv.width = W; cv.height = H;
  const g = cv.getContext("2d");
  g.fillStyle = "#20262b"; g.fillRect(0, 0, W, H);
  const m = (k - 1) / 2, d = W / k;
  for (let r = -m; r <= m; r++) {
    const n = k - Math.abs(r);
    const cy = H / 2 + r * d * 0.866;
    const x0 = (W - n * d) / 2 + d / 2;
    for (let i = 0; i < n; i++) {
      g.beginPath(); g.arc(x0 + i * d, cy, d * 0.47, 0, Math.PI * 2);
      g.fillStyle = color; g.fill();
      g.beginPath(); g.arc(x0 + i * d, cy, d * 0.33, 0, Math.PI * 2);
      g.fillStyle = "#14181b"; g.fill();
    }
  }
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  t.repeat.set(1, 1 / HEX_H);
  t.offset.set(0.5, 0.5);
  return t;
}

const ease = (u) => (u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2);

export default class Konteyner3D {
  emit() {
    this.opts.onState?.({ t: this.t, N: this.N, dir: this.dir, inside: this.inside });
  }

  play(dir) {
    if (!this.N) return;
    if (this.dir === dir) this.dir = 0;
    else {
      if (dir > 0 && this.t >= this.N) this.t = 0;
      if (dir < 0 && this.t <= 0) this.t = this.N;
      this.dir = dir;
    }
    this.dirty = true;
    this.emit();
  }

  seek(t) {
    this.dir = 0;
    this.t = Math.max(0, Math.min(this.N, t));
    this.dirty = true;
    this.emit();
  }

  setWalls(v) {
    this.showWalls = v;
    if (this.walls) this.walls.visible = v;
  }

  setCog(v) {
    this.showCog = v;
    if (this.cog) this.cog.visible = v;
  }

  setClearance(v) {
    this.showClearance = v;
    if (this.clearance) this.clearance.visible = v;
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    this.ro?.disconnect();
    this.el.removeEventListener("pointermove", this._onMove);
    this.el.removeEventListener("pointerleave", this._onLeave);
    this.clear();
    this.hexGeo.dispose();
    this.controls.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  /**
   * @param {HTMLElement} el      sahnenin çizileceği kutu
   * @param {object} opts
   *   colorOf(rowId)  -> satır rengi
   *   labelOf(rowId)  -> satır etiketi (ipucu için)
   *   onState({t, N, dir, inside}) -> oynatıcı durumu değişince
   *   tipEl           -> paket ipucu kutusu
   *   lang            -> "tr" | "en" (ipucu ve ölçü yazıları)
   */
  constructor(el, opts = {}) {
    this.el = el;
    this.opts = { colorOf: () => "#4E79A7", labelOf: (id) => id, lang: "tr", ...opts };
    this.txt = METIN[this.opts.lang] || METIN.tr;
    this.tipEl = opts.tipEl || null;
    this.inside = 0;
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    el.prepend(this.renderer.domElement);
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(38, 1, 0.05, 400);
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.maxPolarAngle = Math.PI * 0.495;
    this.hexGeo = makeHexGeometry();

    this.hemi = new THREE.HemisphereLight(0xffffff, 0x50606c, 1.1);
    this.scene.add(this.hemi);
    const sun = new THREE.DirectionalLight(0xffffff, 1.7);
    sun.position.set(-6, 16, 9);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    Object.assign(sun.shadow.camera, { left: -22, right: 22, top: 22, bottom: -22, near: 1, far: 60 });
    this.scene.add(sun);

    this.groundMat = new THREE.MeshStandardMaterial({ roughness: 1 });
    const ground = new THREE.Mesh(new THREE.PlaneGeometry(160, 160), this.groundMat);
    ground.rotation.x = -Math.PI / 2; ground.position.y = -0.002; ground.receiveShadow = true;
    this.scene.add(ground);
    this.grid = new THREE.GridHelper(160, 160);
    this.grid.material.transparent = true; this.grid.material.opacity = 0.12;
    this.scene.add(this.grid);

    this.dyn = new THREE.Group(); this.scene.add(this.dyn);
    this.meshes = [];
    this.t = 0; this.N = 0; this.dir = 0; this.rate = 3; this.dirty = true;
    this.showWalls = true; this.showCog = true; this.showClearance = true;
    this.view = "3d";
    this.applyTheme();

    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(el);
    this.resize();
    this.ray = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    el.addEventListener("pointermove", (this._onMove = (e) => this.hover(e)));
    el.addEventListener("pointerleave", this._onLeave = () => this.tipEl && (this.tipEl.hidden = true));
    let last = performance.now();
    const loop = (now) => {
      const dt = Math.min(0.1, (now - last) / 1000); last = now;
      if (this.dir) {
        this.t += this.dir * this.rate * dt;
        if (this.t >= this.N) { this.t = this.N; this.dir = 0; this.emit(); }
        if (this.t <= 0) { this.t = 0; this.dir = 0; this.emit(); }
        this.dirty = true;
        this.emit();
      }
      if (this.dirty) this.updateInstances();
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  applyTheme() {
    this.scene.background = new THREE.Color(THEME.scene);
    this.groundMat.color.set(THEME.ground);
    this.grid.material.color = new THREE.Color(THEME.edge);
    if (this.result) this.show(this.result, this.no, true);
  }

  resize() {
    const r = this.el.getBoundingClientRect();
    this.renderer.setSize(r.width, r.height, false);
    this.camera.aspect = r.width / Math.max(1, r.height);
    this.camera.updateProjectionMatrix();
  }

  clear() {
    this.dyn.traverse((o) => {
      if (o.geometry && o.geometry !== this.hexGeo) o.geometry.dispose();
      const mats = Array.isArray(o.material) ? o.material : o.material ? [o.material] : [];
      mats.forEach((m) => { m.map?.dispose(); m.dispose(); });
    });
    this.dyn.clear();
    this.meshes = [];
  }

  show(result, no, keepTime = false) {
    const prevT = this.t, prevN = this.N;
    this.result = result; this.no = no;
    this.clear();
    const cont = result.containers.find((x) => x.no === no)?.cont || result.container;
    const L = cont.L / 1000, W = cont.W / 1000, H = cont.H / 1000;
    this.dims = { L, W, H };
    this.buildContainer(L, W, H, cont.doorH / 1000);
    const c = result.containers.find((x) => x.no === no);
    this.c = c;
    if (!c) { this.N = 0; this.t = 0; this.emit(); return; }

    // yükleme sırası ve sahadaki bekleme yerleri
    const order = c.loadOrder;
    const N = order.length;
    this.N = N;
    const idx = new Map(order.map((id, k) => [id, k]));
    const byId = new Map(c.placed.map((p) => [p.id, p]));
    const boy = c.placed.filter((p) => !p.enine), en = c.placed.filter((p) => p.enine);
    const wMax = boy.length ? Math.max(...boy.map((p) => p.w)) / 1000 : 0;
    const hMax = Math.max(...c.placed.map((p) => p.h)) / 1000;
    const perLayer = Math.max(4, Math.min(16, Math.ceil(boy.length / 3)));
    const yardZ0 = W / 2 + 2.2;
    // enine paketler sahada boy yönünde sıralanır, boyuna yığının ötesinde
    const enLenMax = en.length ? Math.max(...en.map((p) => p.len)) / 1000 : 0;
    const enWMax = en.length ? Math.max(...en.map((p) => p.w)) / 1000 : 0;
    const enPer = Math.max(4, Math.min(20, Math.ceil(en.length / 3)));
    const enZ = (boy.length ? yardZ0 + perLayer * (wMax + 0.12) + 1 : yardZ0) + enWMax / 2;
    this.yardDepth = en.length ? enZ + enWMax / 2 : yardZ0 + perLayer * (wMax + 0.12);
    this.maxLen = Math.max(...c.placed.map((p) => p.len)) / 1000;
    let ib = 0, ie = 0;
    const yardIdx = new Map();
    for (const id of [...order].reverse()) { const p = byId.get(id); yardIdx.set(id, p.enine ? ie++ : ib++); }

    this.anim = new Map();
    for (const id of order) {
      const p = byId.get(id);
      const k = idx.get(id);
      const j = yardIdx.get(id); // ilk yüklenecek paket yığının en üstünde
      const len = p.len / 1000, w = p.w / 1000, h = p.h / 1000;
      const target = new THREE.Vector3(p.x / 1000 + len / 2 - L / 2, p.z / 1000 + h / 2, p.y / 1000 + w / 2 - W / 2);
      const doorX = L / 2 + len / 2 + 0.6;
      let yard;
      if (p.enine) {
        const col = j % enPer, lay = Math.floor(j / enPer);
        yard = new THREE.Vector3(L / 2 + 1 + col * (enLenMax + 0.1) + len / 2, lay * (hMax + 0.06) + h / 2, enZ);
      } else {
        const col = j % perLayer, lay = Math.floor(j / perLayer);
        yard = new THREE.Vector3(doorX, lay * (hMax + 0.06) + h / 2, yardZ0 + col * (wMax + 0.12) + w / 2);
      }
      const door = new THREE.Vector3(doorX, target.y, target.z);
      // düz: köşeler yanda; petek: köşe yukarı (X'te 90°); enine: demet ekseni konteyner eninde (Y'de 90°)
      const qx = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI / 2);
      const qy = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
      let rot, scale;
      // arka arkaya dizilen paketler tek parça görünmesin: boy yönünde iki uçtan kısa bir aralık bırak (yalnız görüntü)
      const lv = Math.max(len * 0.9, len - ARALIK);
      if (p.enine && p.petek) { rot = new THREE.Quaternion().multiplyQuaternions(qy, qx); scale = new THREE.Vector3(w, lv / HEX_H, h); }
      else if (p.enine) { rot = qy; scale = new THREE.Vector3(w, h / HEX_H, lv); }
      else if (p.petek) { rot = qx; scale = new THREE.Vector3(lv, w / HEX_H, h); }
      else { rot = new THREE.Quaternion(); scale = new THREE.Vector3(lv, h / HEX_H, w); }
      this.anim.set(id, { k, yard, door, target, scale, rot });
    }

    // satır tipine göre InstancedMesh
    const groups = {};
    for (const p of c.placed) (groups[p.typeKey] ||= []).push(p);
    for (const list of Object.values(groups)) {
      const color = this.opts.colorOf(list[0].rowId);
      const side = new THREE.MeshStandardMaterial({ color, metalness: 0.35, roughness: 0.48, flatShading: true });
      const cap = new THREE.MeshStandardMaterial({ map: capTexture(list[0].k, color), metalness: 0.2, roughness: 0.6 });
      const mesh = new THREE.InstancedMesh(this.hexGeo, [cap, side], list.length);
      mesh.castShadow = true; mesh.receiveShadow = true; mesh.frustumCulled = false;
      mesh.userData.pkgs = list;
      this.dyn.add(mesh);
      this.meshes.push(mesh);
    }

    // takozlar (taban + ara)
    this.beams = null;
    const U = c.U || result.usable;
    const beamList = [];
    const xs = (x0, len) => {
      const out = [x0 + 150, x0 + len - 150];
      const inner = Math.max(0, Math.ceil((len - 300) / 2000) - 1);
      for (let i = 1; i <= inner; i++) out.push(x0 + 150 + ((len - 300) * i) / (inner + 1));
      return out;
    };
    const blocksDone = new Set();
    for (const p of c.placed) {
      let x0 = p.x, x1 = p.x + p.len, y0 = p.y, y1 = p.y + p.w, floor = p.z - U.taban < 1;
      if (p.blockId) {
        if (blocksDone.has(p.blockId)) continue;
        blocksDone.add(p.blockId);
        const ms = c.placed.filter((m) => m.blockId === p.blockId);
        x0 = Math.min(...ms.map((m) => m.x)); x1 = Math.max(...ms.map((m) => m.x + m.len));
        y0 = Math.min(...ms.map((m) => m.y)); y1 = Math.max(...ms.map((m) => m.y + m.w)); floor = true;
      }
      const hgt = floor ? U.taban : U.ara;
      if (hgt < 5) continue;
      const depth = floor ? 80 : 100;
      const yc = (floor ? hgt / 2 : p.z - hgt / 2) / 1000;
      if (p.enine) {
        // enine paket: kirişler konteyner boyu yönünde, paketin iki ucuna yakın
        for (const by of xs(y0, y1 - y0)) beamList.push({ pos: new THREE.Vector3((x0 + x1) / 2000 - L / 2, yc, by / 1000 - W / 2), size: new THREE.Vector3((x1 - x0) / 1000, hgt / 1000, depth / 1000), pkg: floor ? null : p.id });
      } else {
        for (const bx of xs(x0, x1 - x0)) beamList.push({ pos: new THREE.Vector3(bx / 1000 - L / 2, yc, (y0 + y1) / 2000 - W / 2), size: new THREE.Vector3(depth / 1000, hgt / 1000, (y1 - y0) / 1000), pkg: floor ? null : p.id });
      }
    }
    if (beamList.length) {
      const bm = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshStandardMaterial({ color: 0xa47b4f, roughness: 0.85 }), beamList.length);
      bm.castShadow = true; bm.receiveShadow = true; bm.frustumCulled = false;
      this.dyn.add(bm);
      this.beams = { mesh: bm, list: beamList };
    }

    // ağırlık merkezi işareti
    const s = c.stats;
    const cog = new THREE.Group();
    const cx = s.cog.x / 1000 - L / 2, cz = s.cog.y / 1000 - W / 2;
    const accent = new THREE.Color(THEME.accent);
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.16, 0.24, 40), new THREE.MeshBasicMaterial({ color: accent, side: THREE.DoubleSide }));
    ring.rotation.x = -Math.PI / 2; ring.position.set(cx, 0.03, cz);
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, H + 0.5, 10), new THREE.MeshBasicMaterial({ color: accent }));
    pole.position.set(cx, (H + 0.5) / 2, cz);
    const ball = new THREE.Mesh(new THREE.SphereGeometry(0.09, 20, 14), new THREE.MeshBasicMaterial({ color: accent }));
    ball.position.set(cx, s.cog.z / 1000, cz);
    cog.add(ring, pole, ball);
    cog.renderOrder = 10;
    [ring, pole, ball].forEach((m) => { m.material.depthTest = false; m.material.transparent = true; m.material.opacity = 0.9; m.renderOrder = 10; });
    cog.visible = this.showCog;
    this.cog = cog;
    this.dyn.add(cog);

    this.buildClearance(c, L, W, H);

    this.t = keepTime && prevN === N ? prevT : N;
    this.dir = 0;
    this.dirty = true;
    this.emit();
    if (!keepTime) this.setView(this.view, false);
  }

  /** Yükün en üst noktası ile tavan arasındaki mesafe: ölçü çizgisi + etiket. */
  buildClearance(c, L, W, H) {
    this.clearance = null;
    if (!c.placed.length) return;
    const top = c.placed.reduce((a, p) => (p.z + p.h > a.z + a.h ? p : a));
    const zTop = (top.z + top.h) / 1000;
    const gapMm = Math.round(c.stats.tavanBosluk ?? H * 1000 - (top.z + top.h));
    const g = new THREE.Group();
    const accent = new THREE.Color(THEME.accent);
    const mat = new THREE.LineBasicMaterial({ color: accent, depthTest: false, transparent: true });
    const x = Math.min(L / 2 - 0.15, Math.max(-L / 2 + 0.15, (top.x + top.len / 2) / 1000 - L / 2));
    const z = W / 2 + 0.03;
    const pts = [
      [x, zTop, z], [x, H, z],
      [x - 0.18, zTop, z], [x + 0.18, zTop, z],
      [x - 0.18, H, z], [x + 0.18, H, z],
      [x, zTop, z], [x, zTop, z - W], // en üst paketin üst yüzeyi boyunca kılavuz
    ].map((p) => new THREE.Vector3(...p));
    const line = new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(pts), mat);
    line.renderOrder = 11;
    g.add(line);
    // etiket
    const text = this.txt.tavan(fmt(gapMm));
    const cv = document.createElement("canvas");
    const ctx = cv.getContext("2d");
    const fs = 44;
    ctx.font = `700 ${fs}px Inter, system-ui, sans-serif`;
    const tw = Math.ceil(ctx.measureText(text).width);
    cv.width = tw + 48; cv.height = fs + 30;
    ctx.font = `700 ${fs}px Inter, system-ui, sans-serif`;
    ctx.fillStyle = THEME.accent;
    const r = 14, w = cv.width, h = cv.height;
    ctx.beginPath();
    ctx.moveTo(r, 0); ctx.arcTo(w, 0, w, h, r); ctx.arcTo(w, h, 0, h, r); ctx.arcTo(0, h, 0, 0, r); ctx.arcTo(0, 0, w, 0, r);
    ctx.fill();
    ctx.fillStyle = "#ffffff"; ctx.textBaseline = "middle";
    ctx.fillText(text, 24, h / 2 + 2);
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
    const sh = 0.34;
    sp.scale.set((sh * w) / h, sh, 1);
    sp.position.set(x, H + 0.32, z);
    sp.renderOrder = 12;
    g.add(sp);
    g.visible = this.showClearance;
    this.clearance = g;
    this.dyn.add(g);
  }

  buildContainer(L, W, H, doorH) {
    const g = new THREE.Group();
    const wallColor = new THREE.Color(THEME.wall);
    const edgeColor = new THREE.Color(THEME.edge);
    // ahşap taban
    const floor = new THREE.Mesh(new THREE.BoxGeometry(L, 0.04, W), new THREE.MeshStandardMaterial({ color: 0x8a6544, roughness: 0.9 }));
    floor.position.y = -0.02; floor.receiveShadow = true;
    g.add(floor);
    // oluklu duvarlar (yarı saydam)
    const ribTex = (() => {
      const cv = document.createElement("canvas"); cv.width = 64; cv.height = 8;
      const x = cv.getContext("2d");
      const grd = x.createLinearGradient(0, 0, 64, 0);
      grd.addColorStop(0, "#ffffff"); grd.addColorStop(0.5, "#9aa4ab"); grd.addColorStop(1, "#ffffff");
      x.fillStyle = grd; x.fillRect(0, 0, 64, 8);
      const t = new THREE.CanvasTexture(cv); t.wrapS = THREE.RepeatWrapping; return t;
    })();
    const wallMat = (rep) => {
      const t = ribTex.clone(); t.needsUpdate = true; t.repeat.set(rep, 1);
      return new THREE.MeshStandardMaterial({ color: wallColor, map: t, transparent: true, opacity: 0.22, side: THREE.DoubleSide, depthWrite: false, metalness: 0.3, roughness: 0.6 });
    };
    this.walls = new THREE.Group();
    const back = new THREE.Mesh(new THREE.PlaneGeometry(W, H), wallMat(W / 0.28));
    back.rotation.y = Math.PI / 2; back.position.set(-L / 2, H / 2, 0);
    const left = new THREE.Mesh(new THREE.PlaneGeometry(L, H), wallMat(L / 0.28));
    left.position.set(0, H / 2, -W / 2);
    const right = left.clone(); right.material = wallMat(L / 0.28); right.position.z = W / 2;
    const roof = new THREE.Mesh(new THREE.PlaneGeometry(L, W), new THREE.MeshStandardMaterial({ color: wallColor, transparent: true, opacity: 0.08, side: THREE.DoubleSide, depthWrite: false }));
    roof.rotation.x = -Math.PI / 2; roof.position.y = H;
    this.walls.add(back, left, right, roof);
    // açık kapı kanatları
    for (const sgn of [1, -1]) {
      const pivot = new THREE.Group();
      pivot.position.set(L / 2, H / 2, (sgn * W) / 2);
      const leaf = new THREE.Mesh(new THREE.BoxGeometry(0.05, H, W / 2), new THREE.MeshStandardMaterial({ color: wallColor, transparent: true, opacity: 0.35, metalness: 0.4, roughness: 0.5, depthWrite: false }));
      leaf.position.z = (-sgn * W) / 4;
      pivot.add(leaf);
      pivot.rotation.y = sgn * THREE.MathUtils.degToRad(-100);
      this.walls.add(pivot);
    }
    this.walls.visible = this.showWalls;
    g.add(this.walls);
    // çerçeve
    const frame = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(L, H, W)), new THREE.LineBasicMaterial({ color: edgeColor }));
    frame.position.y = H / 2;
    g.add(frame);
    // kapı yüksekliği çizgisi
    const dl = new THREE.Line(new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(L / 2, doorH, -W / 2), new THREE.Vector3(L / 2, doorH, W / 2)]),
      new THREE.LineDashedMaterial({ color: new THREE.Color(THEME.accent), dashSize: 0.12, gapSize: 0.08 }));
    dl.computeLineDistances();
    g.add(dl);
    this.dyn.add(g);
  }

  updateInstances() {
    this.dirty = false;
    const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), v = new THREE.Vector3();
    let inside = 0;
    for (const mesh of this.meshes) {
      const list = mesh.userData.pkgs;
      for (let i = 0; i < list.length; i++) {
        const a = this.anim.get(list[i].id);
        const u = Math.max(0, Math.min(1, this.t - a.k));
        if (u >= 1) inside++;
        const SPLIT = 0.42;
        if (u <= SPLIT) {
          const s = ease(u / SPLIT);
          v.lerpVectors(a.yard, a.door, s);
          v.y += Math.sin(Math.PI * s) * 0.9;
        } else {
          const s = ease((u - SPLIT) / (1 - SPLIT));
          v.lerpVectors(a.door, a.target, s);
        }
        m4.compose(v, a.rot || q, a.scale);
        mesh.setMatrixAt(i, m4);
      }
      mesh.instanceMatrix.needsUpdate = true;
      mesh.boundingSphere = null;
    }
    // takozlar: taban takozu baştan yerinde, ara takoz üstündeki paket içeri girerken görünür
    if (this.beams) {
      const zero = new THREE.Vector3(0, 0, 0);
      this.beams.list.forEach((b, i) => {
        let vis = true;
        if (b.pkg) {
          const a = this.anim.get(b.pkg);
          vis = this.t - a.k > 0.42;
        }
        m4.compose(b.pos, q, vis ? b.size : zero);
        this.beams.mesh.setMatrixAt(i, m4);
      });
      this.beams.mesh.instanceMatrix.needsUpdate = true;
    }
    this.inside = inside;
    if (!this.dir) this.emit();
  }

  setView(name, animate = true) {
    this.view = name;
    const { L, W, H } = this.dims || { L: 12, W: 2.35, H: 2.7 };
    const yd = this.yardDepth || W / 2 + 6;
    const views = {
      "3d": { pos: [L * 0.5 + 3.5, H * 1.9 + 2, yd * 0.55 + L * 0.42], tgt: [L * 0.12, H * 0.35, yd * 0.3] },
      kapi: { pos: [L / 2 + 5.2, H * 0.72, -0.35], tgt: [0, H * 0.45, 0] },
      yan: { pos: [0, H * 0.85, -(W / 2 + L * 0.82 + 1)], tgt: [0, H * 0.4, 0] },
      ust: { pos: [L * 0.12, L * 1.05 + 6, yd * 0.4 + 0.01], tgt: [L * 0.12, 0, yd * 0.4] },
    };
    const v = views[name];
    // dar ekranda kamerayı geri çek
    const f = Math.max(1, 1.35 / this.camera.aspect);
    const tgt = new THREE.Vector3(...v.tgt);
    this.camera.position.copy(new THREE.Vector3(...v.pos).sub(tgt).multiplyScalar(f).add(tgt));
    this.controls.target.copy(tgt);
    this.controls.update();
  }

  hover(e) {
    const tip = this.tipEl;
    if (!tip) return;
    if (!this.meshes.length) { tip.hidden = true; return; }
    const r = this.el.getBoundingClientRect();
    this.mouse.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    this.ray.setFromCamera(this.mouse, this.camera);
    const hit = this.ray.intersectObjects(this.meshes, false)[0];
    if (!hit) { tip.hidden = true; return; }
    const p = hit.object.userData.pkgs[hit.instanceId];
    const T = this.txt;
    tip.innerHTML = `<b>${esc(this.opts.labelOf(p.rowId))}</b><br>${fmt(p.pieces)} ${T.parca}${p.partial ? ` (${T.kucuk})` : ""} · ${fmt(p.kg)} kg<br>
      <span class="mono">${fmt(p.w)} × ${fmt(p.h)} ${T.kesit}</span><br>
      <span class="mono">x ${fmt(p.x)} · y ${fmt(p.y)} · z ${fmt(p.z)}</span><br>
      <span class="muted">${T.sira} ${fmt(this.anim.get(p.id).k + 1)}</span>`;
    tip.hidden = false;
    const tx = Math.min(e.clientX - r.left + 14, r.width - 270);
    tip.style.left = `${Math.max(8, tx)}px`;
    tip.style.top = `${Math.min(e.clientY - r.top + 14, r.height - 120)}px`;
  }
}

